import {ConfirmDialog, showNotification, useDialog} from 'platform/components';
import {match, Pattern} from 'ts-pattern';

import {useRef, useState} from 'react';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetAutotracerPlConfigQuery} from '@dms/api/core';
import {
  SourcingCebiaReportStatus,
  useCreateSourcingCebiaReportMutation,
  useRegenerateSourcingCebiaReportMutation,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {handleApiError, usePermissions} from '@dms/shared';

import {ApiError, Nullish} from 'shared/web';

import {validateVin} from 'features/vin-validate';

import {CEBIA_BRAND} from '../constants/CEBIA_BRAND';
import {useSourcingCebiaReport} from './useSourcingCebiaReport';

function getFailureReason(
  status: SourcingCebiaReportStatus | undefined,
  failureMessage: string | Nullish
) {
  return match(status)
    .with(
      'error',
      () => failureMessage || i18n.t('page.integrations.labels.reportGenerationFailed')
    )
    .with('canceled', () => failureMessage || i18n.t('general.labels.canceled'))
    .otherwise(() => null);
}

function getUnavailableReason(
  status: SourcingCebiaReportStatus | undefined,
  canRegenerate: boolean | undefined,
  failureReason: string | null
) {
  return match(status)
    .with('accepted', 'pending', () => i18n.t('aiAssistant.status.preparingReport'))
    .with('error', 'canceled', () => (canRegenerate ? null : failureReason))
    .otherwise(() => null);
}

function getActionLabel(failureReason: string | null) {
  if (!failureReason) {
    return CEBIA_BRAND;
  }

  const normalized = failureReason.replace(/\s+/g, ' ').trim();
  const snippet = normalized.length > 24 ? `${normalized.slice(0, 24)}…` : normalized;
  return `${CEBIA_BRAND}: ${snippet}`;
}

export function useSourcingCebiaReportAction(props: {
  externalId: string | null;
  vin: string | null;
}) {
  const {
    report,
    isLoading: isStatusLoading,
    isError: isStatusError,
    canReadReport,
  } = useSourcingCebiaReport(props.externalId);
  const {
    data: config,
    isLoading: isConfigLoading,
    isError: isConfigError,
  } = useGetAutotracerPlConfigQuery(undefined, {skip: props.externalId === null});
  const [canCreateReport] = usePermissions({permissionKeys: ['createAutotracerPl']});
  const [createReport, {isLoading: isCreating}] = useCreateSourcingCebiaReportMutation();
  const [regenerateReport, {isLoading: isRegenerating}] =
    useRegenerateSourcingCebiaReportMutation();
  const [isConfirmOpen, openConfirm, closeConfirm] = useDialog();
  const requestIdRef = useRef<{externalId: string; requestId: string} | null>(null);
  const [submittedReport, setSubmittedReport] = useState<{externalId: string; id: string} | null>(
    null
  );

  const isReady = report?.status === 'completed';
  const isWaitingForStatus =
    submittedReport?.externalId === props.externalId && report?.id !== submittedReport.id;
  const vin = props.vin?.trim().toUpperCase();
  const failureReason = getFailureReason(report?.status, report?.failureMessage);
  const unavailableReason = getUnavailableReason(
    report?.status,
    report?.canRegenerate,
    failureReason
  );
  const disabledReason = match({
    isStatusLoading,
    isStatusError,
    canReadReport,
    unavailableReason,
    isConfigLoading,
    isConfigError,
    isEnabled: config?.enabled,
    canCreateReport,
    hasVin: isNotNilOrEmpty(vin),
    isVinValid: isNotNilOrEmpty(vin) && validateVin(vin ?? ''),
    isCreating,
    isRegenerating,
    isWaitingForStatus,
  })
    .with({isWaitingForStatus: true}, () => i18n.t('aiAssistant.status.preparingReport'))
    .with({isStatusLoading: true}, () => i18n.t('general.labels.loading'))
    .with({isStatusError: true}, () => i18n.t('general.label.featureNotEnabled'))
    .with({canReadReport: false}, () => i18n.t('general.labels.noPermission'))
    .with({unavailableReason: Pattern.not(null)}, ({unavailableReason}) => unavailableReason)
    .with({isConfigLoading: true}, () => i18n.t('general.labels.loading'))
    .with({isConfigError: true}, () => i18n.t('general.label.featureNotEnabled'))
    .with({isEnabled: false}, () => i18n.t('general.label.featureNotEnabled'))
    .with({canCreateReport: false}, () => i18n.t('general.labels.noPermission'))
    .with({hasVin: false}, () => i18n.t('entity.vehicle.labels.noVin'))
    .with({isVinValid: false}, () => i18n.t('entity.vehicle.notifications.vinIsInvalid'))
    .with({isCreating: true}, () => i18n.t('aiAssistant.status.preparingReport'))
    .with({isRegenerating: true}, () => i18n.t('aiAssistant.status.preparingReport'))
    .otherwise(() => null);

  const submit = (generateNew: boolean) => {
    if (disabledReason !== null || props.externalId === null) {
      return Promise.resolve();
    }

    const externalId = props.externalId;
    const requestIdStorageKey = `sourcing-cebia-regenerate-request:${externalId}`;
    let requestId: string | null = null;
    if (generateNew) {
      requestId =
        requestIdRef.current?.externalId === externalId
          ? requestIdRef.current.requestId
          : (localStorage.getItem(requestIdStorageKey) ?? crypto.randomUUID());
      requestIdRef.current = {externalId, requestId};
      localStorage.setItem(requestIdStorageKey, requestId);
    }
    const request =
      requestId === null ? createReport({externalId}) : regenerateReport({externalId, requestId});

    return request
      .unwrap()
      .then((createdReport) => {
        setSubmittedReport({externalId, id: createdReport.id});
        requestIdRef.current = null;
        if (generateNew) {
          localStorage.removeItem(requestIdStorageKey);
        }
        match(createdReport.status)
          .with('completed', () =>
            showNotification.success(i18n.t('analytics.notifications.reportCreated'))
          )
          .with('accepted', 'pending', () =>
            showNotification.info(i18n.t('aiAssistant.status.preparingReport'))
          )
          .with('error', 'canceled', () =>
            showNotification.error(
              getFailureReason(createdReport.status, createdReport.failureMessage) ||
                i18n.t('page.integrations.labels.reportGenerationFailed')
            )
          )
          .exhaustive();
      })
      .catch((error: ApiError) => handleApiError(error));
  };

  const onClick = () => {
    if (disabledReason !== null) {
      return;
    }

    if (
      isReady ||
      ((report?.status === 'error' || report?.status === 'canceled') && report.canRegenerate)
    ) {
      openConfirm();
    } else {
      void submit(false);
    }
  };

  const tooltipReason = disabledReason ?? failureReason;

  return {
    label: getActionLabel(
      isWaitingForStatus || isCreating || isRegenerating ? null : failureReason
    ),
    isDisabled: disabledReason !== null,
    tooltip: tooltipReason === null ? undefined : {label: tooltipReason},
    isLoading: isCreating || isRegenerating,
    onClick,
    confirmDialog: (
      <ConfirmDialog
        isOpen={isConfirmOpen}
        onClose={closeConfirm}
        onConfirm={() => submit(true)}
        text={`${i18n.t('analytics.actions.generateReport')} ${CEBIA_BRAND} (${i18n.t('general.labels.new')})?`}
      />
    ),
  };
}
